import React, { useEffect, useState, useCallback } from 'react';
import { OktaAuth } from '@okta/okta-auth-js';
import { Text, View, ImageBackground, StyleSheet, Dimensions } from 'react-native';
import { CTAButton } from './screens/components';

import App from './App';
import I18n from './I18n';
import { THEME } from './constants/styles';
import { OKTA_CLIENT_ID, env } from './constants/env';

const config = {
  issuer: 'https://integrator-2783557.okta.com/oauth2/default',
  clientId: env(OKTA_CLIENT_ID) || '0oatd76gs728lyqMG697',
  scopes: ['openid', 'profile', 'email'],
  redirectUri: window.location.origin + '/login/callback',
  postLogoutRedirectUri: window.location.origin + '/logout/callback',
};

function stripQueryParam(param) {
  const url = new URL(window.location.href);
  const found = url.searchParams.get(param) !== null;
  url.searchParams.delete(param);

  // Replace URL without triggering navigation
  window.history.replaceState({}, '', url.toString());
  return found;
}
function addRedirectFlag(urlString) {
  const url = new URL(urlString, window.location.origin);
  url.searchParams.set('redirected', 'true'); // or '1'
  return url.pathname + url.search; // relative URL only
}

const authClient = new OktaAuth(config);

const SCREEN_WIDTH_VERTICAL = 800;

const ProtectedApp = () => {
  const [token, setToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [buttonClicked, setButtonClicked] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const { width } = Dimensions.get('window');
  const [isVerticalLayout, setIsVerticalLayout] = useState(width < SCREEN_WIDTH_VERTICAL);

  const checkAuthentication = async () => {
    try {
      if (authClient.isLoginRedirect()) {
        // console.info('Handling redirect callback');
        setRedirecting(true);
        await authClient.handleRedirect();
      } else if (!!(await authClient.isAuthenticated())) {
        // console.info('User is authenticated', window.location.href);
        const idToken = await authClient.tokenManager.get('idToken');
        // console.info('ID Token claims:', idToken, idToken?.claims);
        // User is authenticated
        setToken(idToken);
        setUserData({
          firstName: idToken?.claims?.first_Name,
          lastName: idToken?.claims?.last_Name,
          email: idToken?.claims?.email,
          userType: idToken?.claims?.user_type,
          employeeType: idToken?.claims?.employee_type,
          rank: idToken?.claims?.Rank,
          serviceBranch: idToken?.claims?.User_organization,
          dodId: idToken?.claims?.EDIPI,
        });
        if (stripQueryParam('redirected')) {
          // do whatever logic you need
          setButtonClicked(true);
        }
        setRedirecting(false);
        //throw new Error('No token found after authentication');
      } else {
        setToken(null);
        setTokenError(null);
        setUserData(null);
        setRedirecting(false);
        stripQueryParam('redirected');
        setButtonClicked(false);
      }
    } catch (e) {
      console.info('Error handling redirect', e);
      // log or display error details
      setToken(null);
      setUserData(null);
      setTokenError(e);
      setButtonClicked(false);
    }
  };

  const beginAuthentication = useCallback(async () => {
    if (!!(await authClient.isAuthenticated())) {
      setButtonClicked(true);
    } else {
      if (token) setToken(null);
      const original = window.location.pathname + window.location.search;
      authClient.setOriginalUri(addRedirectFlag(original));
      authClient.signInWithRedirect();
    }
  }, [token]);

  useEffect(() => {
    if (window.location.pathname.endsWith('/logout/callback')) {
      // redirect to window.location.origin
      window.location.href = window.location.origin;
      return;
    }
    checkAuthentication();
  }, []);

  const handleLogout = useCallback(async function handleLogout() {
    setToken(null);
    await authClient.signOut();
    setButtonClicked(false);
  }, []);

  const handleDimsChange = useCallback(({ window: { width, height } }) => {
    setIsVerticalLayout(width < SCREEN_WIDTH_VERTICAL);
  }, []);

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', handleDimsChange);
    return () => {
      subscription.remove();
    };
  }, [handleDimsChange]);

  let tempToken = token;
  // tempToken = null;

  if (!tempToken || !buttonClicked) {
    return (
      <View style={isVerticalLayout ? styles.containerVertical : styles.containerHorizontal}>
        <View
          style={
            isVerticalLayout ? styles.imageBackgroundVertical : styles.imageBackgroundHorizontal
          }
        >
          <ImageBackground
            style={{ width: '100%', height: '100%' }}
            source={require('../resources/auth_bg/web-bg-login.jpg')}
          />
        </View>
        <View style={{ flex: 1 }}>
          <View style={styles.paneWrapper}>
            <View style={{}}>
              <Text style={styles.termsText}>{I18n.t('airforce.terms')}</Text>
              {!!tokenError && (
                <Text style={THEME.errorMessage}>
                  {tokenError?.message || I18n.t('auth.militaryText')}
                </Text>
              )}
              {!redirecting && (
                <CTAButton onPress={beginAuthentication} label={tempToken ? 'Enter' : 'Log In'} />
              )}
            </View>
          </View>
        </View>
      </View>
    );
  }
  return <App oktaToken={tempToken} userData={userData} onLogout={handleLogout} />;
};
const styles = StyleSheet.create({
  containerVertical: {
    position: 'relative',
    minHeight: '100%',
  },
  containerHorizontal: {
    flex: 1,
    flexDirection: 'row',
  },
  imageBackgroundHorizontal: {
    flex: 1,
    minHeight: '100%',
  },
  imageBackgroundVertical: {
    width: '100%',
    height: 120,
  },
  paneWrapper: {
    flex: 1,
    justifyContent: 'center',
    maxWidth: 400,
    width: '100%',
    marginHorizontal: 'auto',
  },
  termsText: {
    ...THEME.textRegular,
    marginBottom: 25,
  },
});
export default ProtectedApp;
